import React, { createContext, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { BranchToWorkspaceModal } from "./BranchToWorkspaceDialog";
import CreateDatasetDialog from "./CreateDatasetDialog";
import CreateEvaluationDialog from "./CreateEvaluationDialog";
import CreateFolderDialog from "./CreateFolderDialog";
import CreateReleaseLabelDialog from "./CreateReleaseLabelDialog";
import { DuplicateEntityDialog } from "./DuplicateDialog";
import { useDeleteEntity } from "./hooks/useDeleteEntity";
import { MoveToFolderDialog } from "./MoveToFolderDialog";
import NameEntityDialog from "./NameEntityDialog";
import RenameEntityDialog from "./RenameEntityDialog";
import { WorkspaceIntroDialog } from "./WorkspaceIntroDialog";

import { useUser } from "@/context/user-context";
import { Entity, EntityType, getRequiredEntityNumericId } from "@/queries/folder-entities/types";
import DuplicateSmartTableDialog from "@/routes/smart-table/components/DuplicateSmartTableDialog";
import { PromptRegistry } from "@/types/prompt-registry";

interface DialogState {
  createFolder: {
    open: boolean;
    parentFolderId?: number | null;
    redirect?: boolean;
  };

  moveFolder: {
    open: boolean;
    entities?: Entity[];
  };

  renameEntity: {
    open: boolean;
    entity?: Entity | null;
  };

  selectReleaseLabel: {
    open: boolean;
    onSubmit?: (promptLabelName: string, type: "prompt" | "workflow") => void;
    isLoading?: boolean;
  };

  nameEntity: {
    open: boolean;
    entityType?: "workflow" | "prompt" | "dataset" | "evaluation";
    onConfirm?: (name: string) => void;
  };

  createDataset: {
    open: boolean;
  };

  createEvaluation: {
    open: boolean;
    isLoading?: boolean;
    currentFolderId?: number | null;
  };

  duplicateEntity: {
    open: boolean;
    entity?: Entity | null;
  };

  branchToWorkspace: {
    open: boolean;
    prompt?: Entity | null;
  };
}

interface DialogActions {
  openCreateFolder: (parentFolderId?: number | null, redirect?: boolean) => void;
  closeCreateFolder: () => void;
  openMoveFolder: (entities: Entity | Entity[]) => void;
  closeMoveFolder: () => void;
  openSelectReleaseLabel: () => void;
  closeSelectReleaseLabel: () => void;
  openNameEntity: (
    entityType: "workflow" | "prompt" | "dataset" | "evaluation",
    onConfirm: (name: string) => void,
  ) => void;
  closeNameEntity: () => void;
  openCreateDataset: () => void;
  closeCreateDataset: () => void;
  openCreateEvaluation: (currentFolderId?: number | null) => void;
  closeCreateEvaluation: () => void;
  openRenameEntity: (entity: Entity) => void;
  closeRenameEntity: () => void;
  deleteEntity: (entity: Entity) => Promise<void>;
  deleteBatch: (entities: Entity[], onSuccess?: () => void) => Promise<void>;
  openDuplicateEntity: (entity: Entity) => void;
  closeDuplicateEntity: () => void;
  openBranchToWorkspace: (prompt: Entity) => void;
  closeBranchToWorkspace: () => void;
  closeAllDialogs: () => void;
}

interface DialogContextType extends DialogState, DialogActions {
  onSuccess?: () => void;
  setOnSuccess: (callback: () => void) => void;
}

const initialDialogState: DialogState = {
  createFolder: {
    open: false,
    redirect: true,
  },
  moveFolder: {
    open: false,
    entities: [],
  },

  selectReleaseLabel: {
    open: false,
  },
  nameEntity: {
    open: false,
  },
  createDataset: {
    open: false,
  },
  createEvaluation: {
    open: false,
  },
  renameEntity: {
    open: false,
  },
  duplicateEntity: {
    open: false,
  },
  branchToWorkspace: {
    open: false,
  },
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useRegistryDialogs = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useRegistryDialogs must be used within a DialogProvider");
  }
  return context;
};

interface DialogProviderProps {
  children: React.ReactNode;
  onSuccess?: () => void;
}

const DialogProvider: React.FC<DialogProviderProps> = ({
  children,
  onSuccess: defaultOnSuccess,
}) => {
  const [dialogState, setDialogState] = useState<DialogState>(initialDialogState);
  const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>(() => defaultOnSuccess);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [releaseLabelLoading, setReleaseLabelLoading] = useState(false);
  const userContext = useUser();

  const { deleteEntity, deleteBatch } = useDeleteEntity();

  const openCreateFolder = (parentFolderId?: number | null, redirect = true) => {
    setDialogState(prev => ({
      ...prev,
      createFolder: {
        open: true,
        parentFolderId,
        redirect,
      },
    }));
  };

  const closeCreateFolder = () => {
    setDialogState(prev => ({
      ...prev,
      createFolder: {
        ...prev.createFolder,
        open: false,
      },
    }));
  };

  const openMoveFolder = (entities: Entity | Entity[]) => {
    const entityArray = Array.isArray(entities) ? entities : [entities];
    setDialogState(prev => ({
      ...prev,
      moveFolder: {
        open: true,
        entities: entityArray,
      },
    }));
  };

  const closeMoveFolder = () => {
    setDialogState(prev => ({
      ...prev,
      moveFolder: {
        open: false,
        entities: [],
      },
    }));
  };

  const openSelectReleaseLabel = () => {
    setDialogState(prev => ({
      ...prev,
      selectReleaseLabel: {
        open: true,
      },
    }));
  };

  const closeSelectReleaseLabel = () => {
    setDialogState(prev => ({
      ...prev,
      selectReleaseLabel: {
        open: false,
      },
    }));
  };

  const openNameEntity = (
    entityType: "workflow" | "prompt" | "dataset" | "evaluation",
    onConfirm: (name: string) => void,
  ) => {
    setDialogState(prev => ({
      ...prev,
      nameEntity: {
        open: true,
        entityType,
        onConfirm,
      },
    }));
  };

  const closeNameEntity = () => {
    setDialogState(prev => ({
      ...prev,
      nameEntity: {
        ...prev.nameEntity,
        open: false,
      },
    }));
  };

  const openCreateDataset = () => {
    setDialogState(prev => ({
      ...prev,
      createDataset: {
        open: true,
      },
    }));
  };

  const closeCreateDataset = () => {
    setDialogState(prev => ({
      ...prev,
      createDataset: {
        ...prev.createDataset,
        open: false,
      },
    }));
  };

  const openCreateEvaluation = (currentFolderId?: number | null) => {
    setDialogState(prev => ({
      ...prev,
      createEvaluation: {
        open: true,
        currentFolderId,
      },
    }));
  };

  const closeCreateEvaluation = () => {
    setDialogState(prev => ({
      ...prev,
      createEvaluation: {
        ...prev.createEvaluation,
        open: false,
      },
    }));
  };

  const openRenameEntity = (entity: Entity) => {
    setDialogState(prev => ({
      ...prev,
      renameEntity: {
        open: true,
        entity,
      },
    }));
  };

  const closeRenameEntity = () => {
    setDialogState(prev => ({
      ...prev,
      renameEntity: {
        open: false,
        entity: null,
      },
    }));
  };

  const openDuplicateEntity = (entity: Entity) => {
    setDialogState(prev => ({
      ...prev,
      duplicateEntity: {
        open: true,
        entity,
      },
    }));
  };

  const closeDuplicateEntity = () => {
    setDialogState(prev => ({
      ...prev,
      duplicateEntity: {
        open: false,
        entity: null,
      },
    }));
  };

  const openBranchToWorkspace = (prompt: Entity) => {
    setDialogState(prev => ({
      ...prev,
      branchToWorkspace: {
        open: true,
        prompt,
      },
    }));
  };

  const closeBranchToWorkspace = () => {
    setDialogState(prev => ({
      ...prev,
      branchToWorkspace: {
        open: false,
        prompt: null,
      },
    }));
  };

  const closeAllDialogs = () => {
    setDialogState(initialDialogState);
  };

  const entityToPromptRegistry = (entity: Entity): PromptRegistry => ({
    id: getRequiredEntityNumericId(entity.id),
    prompt_name: entity.name,
    created_at: entity.created_at || new Date().toISOString(),
    folder_id: entity.folder_id || null,
    last_version_created_at: entity.updated_at || entity.created_at || new Date().toISOString(),
    workspace_id: userContext.activeWorkspaceId!,
    tags: [],
    description: "",
    folder_name: null,
    folder_path: null,
    folder_parent_id: null,
    is_snippet: entity.type === EntityType.SNIPPET,
  });

  const handleDialogSuccess = () => {
    onSuccess?.();
  };

  const contextValue: DialogContextType = {
    ...dialogState,
    createEvaluation: {
      ...dialogState.createEvaluation,
      isLoading: evaluationLoading,
    },
    selectReleaseLabel: {
      ...dialogState.selectReleaseLabel,
      isLoading: releaseLabelLoading,
    },
    openCreateFolder,
    closeCreateFolder,
    openMoveFolder,
    closeMoveFolder,
    openSelectReleaseLabel,
    closeSelectReleaseLabel,
    openNameEntity,
    closeNameEntity,
    openCreateDataset,
    closeCreateDataset,
    openCreateEvaluation,
    closeCreateEvaluation,
    openRenameEntity,
    closeRenameEntity,
    openDuplicateEntity,
    closeDuplicateEntity,
    openBranchToWorkspace,
    closeBranchToWorkspace,
    closeAllDialogs,
    onSuccess,
    setOnSuccess: (callback: () => void) => setOnSuccess(() => callback),
    deleteEntity,
    deleteBatch,
  };

  const { folderId } = useParams();
  const isDuplicatingSmartTable =
    dialogState.duplicateEntity.entity?.type === EntityType.SMART_TABLE;

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      <CreateFolderDialog
        open={dialogState.createFolder.open}
        redirect={dialogState.createFolder.redirect}
        onOpenChange={closeCreateFolder}
        parentFolderId={dialogState.createFolder.parentFolderId}
        onSuccess={handleDialogSuccess}
      />

      <MoveToFolderDialog
        key={folderId}
        open={dialogState.moveFolder.open}
        setOpen={closeMoveFolder}
        entities={dialogState.moveFolder.entities || []}
      />

      <CreateReleaseLabelDialog
        isOpen={dialogState.selectReleaseLabel.open}
        setOpen={closeSelectReleaseLabel}
        onSubmit={dialogState.selectReleaseLabel.onSubmit || (() => undefined)}
        onLoadingChange={setReleaseLabelLoading}
      />

      <NameEntityDialog
        open={dialogState.nameEntity.open}
        onOpenChange={closeNameEntity}
        entityType={dialogState.nameEntity.entityType || "workflow"}
        onConfirm={dialogState.nameEntity.onConfirm || (() => undefined)}
      />

      <CreateDatasetDialog
        open={dialogState.createDataset.open}
        onOpenChange={closeCreateDataset}
        onSuccess={handleDialogSuccess}
      />

      <CreateEvaluationDialog
        open={dialogState.createEvaluation.open}
        onOpenChange={closeCreateEvaluation}
        onSuccess={handleDialogSuccess}
        onLoadingChange={setEvaluationLoading}
        currentFolderId={dialogState.createEvaluation.currentFolderId}
      />

      <RenameEntityDialog
        open={dialogState.renameEntity.open}
        onOpenChange={closeRenameEntity}
        entity={dialogState.renameEntity.entity || null}
        onSuccess={handleDialogSuccess}
      />

      <DuplicateEntityDialog
        open={dialogState.duplicateEntity.open && !isDuplicatingSmartTable}
        entity={dialogState.duplicateEntity.entity || null}
        onClose={closeDuplicateEntity}
        onSuccess={handleDialogSuccess}
      />

      <DuplicateSmartTableDialog
        open={dialogState.duplicateEntity.open && isDuplicatingSmartTable}
        tableId={isDuplicatingSmartTable ? String(dialogState.duplicateEntity.entity?.id) : null}
        tableTitle={dialogState.duplicateEntity.entity?.name}
        onClose={closeDuplicateEntity}
        onSuccess={handleDialogSuccess}
      />

      <BranchToWorkspaceModal
        open={dialogState.branchToWorkspace.open}
        prompt={
          dialogState.branchToWorkspace.prompt
            ? entityToPromptRegistry(dialogState.branchToWorkspace.prompt)
            : undefined
        }
        setOpen={closeBranchToWorkspace}
      />

      <WorkspaceIntroDialog />
    </DialogContext.Provider>
  );
};

export const useDialogTriggers = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogTriggers must be used within a DialogProvider");
  }

  return {
    createFolder: context.openCreateFolder,
    moveFolder: context.openMoveFolder,
    moveFolders: (entities: Entity[]) => context.openMoveFolder(entities),
    selectReleaseLabel: context.openSelectReleaseLabel,
    nameEntity: context.openNameEntity,
    createDataset: context.openCreateDataset,
    createEvaluation: context.openCreateEvaluation,
    renameEntity: context.openRenameEntity,
    deleteEntity: context.deleteEntity,
    deleteBatch: context.deleteBatch,
    duplicateEntity: context.openDuplicateEntity,
    branchToWorkspace: context.openBranchToWorkspace,
    closeAll: context.closeAllDialogs,
  };
};

export const useDialogStates = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogStates must be used within a DialogProvider");
  }

  return {
    isCreateFolderOpen: context.createFolder.open,
    isMoveFolderOpen: context.moveFolder.open,
    isSelectReleaseLabelOpen: context.selectReleaseLabel.open,
    isNameEntityOpen: context.nameEntity.open,
    isCreateDatasetOpen: context.createDataset.open,
    isCreateEvaluationOpen: context.createEvaluation.open,
    isCreateEvaluationLoading: context.createEvaluation.isLoading || false,
    isSelectReleaseLabelLoading: context.selectReleaseLabel.isLoading || false,
    isRenameEntityOpen: context.renameEntity.open,
    isBranchToWorkspaceOpen: context.branchToWorkspace.open,
    isAnyDialogOpen:
      context.createFolder.open ||
      context.moveFolder.open ||
      context.selectReleaseLabel.open ||
      context.nameEntity.open ||
      context.createDataset.open ||
      context.createEvaluation.open ||
      context.renameEntity.open ||
      context.branchToWorkspace.open,
  };
};

export {
  BranchToWorkspaceModal,
  CreateDatasetDialog,
  CreateEvaluationDialog,
  CreateFolderDialog,
  CreateReleaseLabelDialog,
  MoveToFolderDialog,
  NameEntityDialog,
  RenameEntityDialog,
};

export type { DialogActions, DialogState };

export default DialogProvider;
